import { type Breakpoint } from '../types/layout';
import { type InteractionEvent } from '../types/craft';

export const MAX_PADDING: Record<Breakpoint, number> = {
  desktop: 100,
  tablet: 20,
  mobile: 10,
};
export const DESKTOP_WIDTH = 1200;
export const TABLET_WIDTH = 768;
export const MOBILE_WIDTH = 375; // This is only used for builder frontend breakpoint width
export const PAGE_PHANTOM_PADDING = 1200;
export const CONTENT_BLOCK_HEIGHT = 512;
export const IMAGE_NODE_HEIGHT = 512;
export const VIDEO_NODE_HEIGHT = 315; // Default 16:9 aspect ratio at ~560px width
export const EMBED_NODE_HEIGHT = 315; // Default height for embed elements
export const CONTENT_BLOCK_NAME = 'Container';
export const BUTTON_NODE_NAME = 'Button';
export const COUNTDOWN_NODE_NAME = 'Countdown';
export const ICON_NODE_NAME = 'Icon';
export const ICON_DEFAULT_SIZE = 48;
export const ICON_STYLE_SOLID = 'solid' as const;
export const ICON_STYLE_REGULAR = 'regular' as const;
export const TEXT_NODE_NAME = 'Text';
export const PAGE_NODE_NAME = 'Page';
export const IMAGE_NODE_NAME = 'Image';
export const VIDEO_NODE_NAME = 'Video';
export const EMBED_NODE_NAME = 'Embed';
export const FORM_NODE_NAME = 'Form';
/** Default gap (px) between Form children; used for min-height, resize, and form field styles. */
export const FORM_GAP = 12;
export const FORM_TEXT_FIELD_NODE_NAME = 'Form Text Field';
export const FORM_EMAIL_FIELD_NODE_NAME = 'Form Email Field';
export const FORM_PHONE_FIELD_NODE_NAME = 'Form Phone Field';
export const FORM_CONSENT_FIELD_NODE_NAME = 'Form Consent Field';
export const FORM_DROPDOWN_FIELD_NODE_NAME = 'Form Dropdown Field';
export const FORM_SELECTION_FIELD_NODE_NAME = 'Form Selection Field';
export const FORM_SUBMIT_BUTTON_NODE_NAME = 'Form Submit Button';
export const POPUP_NODE_NAME = 'Popup';
export const EXTERNAL_LINK_EVENT = 'open-external-link';
export const OPEN_POPUP_EVENT = 'open-popup';
export const JUMP_TO_SECTION_EVENT = 'jump-to-section';
export const LINK_TO_PAGE_EVENT = 'link-to-page';
export const SELECT_PAGE_EVENT = 'select-page';
export const NO_LINK_EVENT = 'nolink';
export const STAY_ON_PAGE = 'stay-on-page';
export const MIXED_LINK_EVENT = 'mixed-link';
export const DEFAULT_INTERACTION_EVENT: InteractionEvent = {
  eventType: NO_LINK_EVENT,
  eventData: null,
};
export const DEFAULT_EXPIRATION_EVENT: InteractionEvent = {
  eventType: STAY_ON_PAGE,
  eventData: null,
};
export const MOBILE_MEDIA_QUERY_HEADER = `@media (max-width: ${TABLET_WIDTH - 1}px)`;
export const TABLET_MEDIA_QUERY_HEADER = `@media (min-width: ${TABLET_WIDTH}px) and (max-width: ${DESKTOP_WIDTH - 1}px)`;
export const DESKTOP_MEDIA_QUERY_HEADER = `@media (min-width: ${DESKTOP_WIDTH}px)`;

export const MOBILE_CONTAINER_QUERY_HEADER = `@container (max-width: ${TABLET_WIDTH - 1}px)`;
export const TABLET_CONTAINER_QUERY_HEADER = `@container (min-width: ${TABLET_WIDTH}px) and (max-width: ${DESKTOP_WIDTH - 1}px)`;
export const DESKTOP_CONTAINER_QUERY_HEADER = `@container (min-width: ${DESKTOP_WIDTH}px)`;

export const ASSETS_PANEL = {
  SECTIONS: 'Sections',
  SECTION_NAV: 'Section Nav',
  SECTION_HERO: 'Section Hero',
  SAVED_SECTIONS: 'Saved Sections',
  ELEMENTS: 'Elements',
  AI_BUILDER: 'AI Builder',
  SETTINGS: 'Settings',
  LAYERS: 'Layers',
  STYLES: 'Styles',
  STYLES_NEW: 'Styles New',
  STYLES_EDIT: 'Styles Edit',
  INTEGRATIONS: 'Integrations',
} as const;

export const SECTION_CATEGORIES = {
  about: { value: 'about', label: 'About' },
  cta: { value: 'cta', label: 'CTA' },
  contact: { value: 'contact', label: 'Contact' },
  event: { value: 'event', label: 'Event' },
  faq: { value: 'faq', label: 'FAQ' },
  features: { value: 'features', label: 'Features' },
  header: { value: 'header', label: 'Navigation' },
  hero: { value: 'hero', label: 'Hero' },
  logos: { value: 'logos', label: 'Logos' },
  'opt-in-form': { value: 'opt-in-form', label: 'Opt-in Form' },
  pricing: { value: 'pricing', label: 'Pricing' },
  schedule: { value: 'schedule', label: 'Schedule' },
  testimonials: { value: 'testimonials', label: 'Testimonials' },
  popup: { value: 'popup', label: 'Pop-up' },
};

const LEGACY_SECTION_CATEGORY_ALIASES: Record<string, string> = {
  'navigation-bar': SECTION_CATEGORIES.header.value,
  'hero-section': SECTION_CATEGORIES.hero.value,
  'features-overview': SECTION_CATEGORIES.features.value,
  'how-it-works': SECTION_CATEGORIES.about.value,
  'stats-social-proof': SECTION_CATEGORIES.logos.value,
};

export const normalizeSectionGroupId = (sectionGroupId?: string | null): string => {
  if (!sectionGroupId) return '';
  return LEGACY_SECTION_CATEGORY_ALIASES[sectionGroupId] ?? sectionGroupId;
};
export const STYLE_TYPOGRAPHY_VARIANTS = {
  // Heading variants
  'heading.h1': 'H1 - Heading',
  'heading.h2': 'H2 - Heading',
  'heading.h3': 'H3 - Heading',
  'heading.h4': 'H4 - Heading',
  'heading.h5': 'H5 - Heading',
  'heading.h6': 'H6 - Heading',

  // Body variants
  'body.small': 'Small - Body',
  'body.medium': 'Medium - Body',
  'body.large': 'Large - Body',
} as const;

export const PAGE_PUBLISH_ERROR_MESSAGES = {
  INVALID_DOMAIN: (domain: string) => `Domain: ${domain} is invalid.`,
  MISSING_DOMAIN: `Domain field is missing.`,
  INVALID_PATH: (path: string) => `Page slug: ${path} is invalid.`,
  MISSING_PATH: `Page slug is missing.`,
  MISSING_NAME: `Page name is missing.`,
};

const BOUNDING_BOX_PROPS = ['padding', 'margin', 'borderRadius'];
const POSITION_PROPS = [
  'position',
  'positionAnchorsState',
  'left',
  'right',
  'top',
  'bottom',
  'zIndex',
];
const MAX_WIDTH_PROPS = ['maxWidth', 'maxWidthUnit'];
const DETACHABLE_PROPS_REGISTRY: Record<string, string[]> = {
  [PAGE_NODE_NAME]: ['gap'],
  [BUTTON_NODE_NAME]: ['buttonType'],
  [CONTENT_BLOCK_NAME]: [
    'flexDirection',
    'width',
    'height',
    'fillSpace',
    'flexBasis',
    'gap',
    'justifyContent',
    'alignItems',
    'disableMaxWidth',
    'shapeDividerHeight',
  ],
  [TEXT_NODE_NAME]: ['width', 'flexBasis', 'fillSpace'],
  [FORM_NODE_NAME]: ['width', 'height', 'fillSpace', 'flexBasis'],
  [IMAGE_NODE_NAME]: ['width', 'height', 'fillSpace', 'flexBasis'],
  [ICON_NODE_NAME]: ['width', 'height', 'fillSpace', 'flexBasis'],
  [VIDEO_NODE_NAME]: ['width', 'height', 'fillSpace', 'flexBasis'],
  [EMBED_NODE_NAME]: ['width', 'height', 'fillSpace', 'flexBasis'],
};
/**
 * Returns the list of detachable (breakpoint-specific) property names for a given node type.
 * This includes properties registered in DETACHABLE_PROPS_REGISTRY for the node's display name,
 * as well as common position and bounding box properties that are always considered detachable.
 *
 * @param nodeDisplayName - The display name of the node/component (e.g., "Page", "Button", etc.)
 * @returns An array of property names that can have breakpoint-specific overrides for this node
 */

export function getDetachablePropsForNode(nodeDisplayName: string): string[] {
  const props = DETACHABLE_PROPS_REGISTRY[nodeDisplayName] ?? [];
  return [...props, ...POSITION_PROPS, ...BOUNDING_BOX_PROPS, ...MAX_WIDTH_PROPS];
}
